/**
 * Per-agent observations of the effective integration set, kept in the manager
 * process.
 *
 * WHAT THIS FILE USED TO BE, and why the rest of it is gone (ENG-9739).
 *
 * ENG-9652 added a precondition here: refuse to write channel credentials when
 * the channel's "backing integration" was not installed, keyed on a map of five
 * channel -> integration pairs (slack, telegram, msteams, whatsapp, discord).
 *
 * None of those integrations exist, and adding them is not the fix:
 *
 *   "We don't have integrations for channels... They are separate concepts"
 *     — Brad, 2026-09-01
 *
 * Channels and integrations are distinct by design. So the precondition could
 * never be satisfied by anyone, reached its POSITIVE-knowledge branch for every
 * agent whose integration set had been observed, terminated the credential sync,
 * and emitted a message instructing operators to install something with no
 * console entry. Six agents live when it was found, one of them for 8.6 days.
 *
 * It bit only agents whose sync was actively trying, which is why it looked
 * fine: agents that had already converged never re-entered the path, and NEW
 * agents failed immediately. A customer messaged a brand-new bot three times and
 * received nothing while the console showed "Token valid".
 *
 * The guard was written to fail open — "unknown always retries" — and never got
 * the chance, because a map of relationships that do not exist turns every
 * unknown into a certainty. A fail-open design cannot rescue a false premise in
 * its own table.
 *
 * REMOVED RATHER THAN EMPTIED. An empty map plus a parity test was the first fix
 * and it was too timid: it leaves the concept in place, and a concept the
 * architecture says should not exist is an invitation to refill it. What remains
 * below is only the observation bookkeeping, which several other callers use.
 *
 * WHAT THIS DOES NOT SOLVE. ENG-9652's complaint was real — an agent spent 16.5
 * hours and 198 attempts on a write that could not land, and the worst case in
 * that sweep was ~1,800 attempts over a week. Removing the guard restores that
 * retry. The cause of those non-convergences is still undiagnosed, and guessing
 * a terminal condition is exactly what produced this outage, so it needs its own
 * ticket and a measurement rather than another premise.
 */

export type EffectiveIntegrationObservations = Map<string, ReadonlySet<string>>;

export function createEffectiveIntegrationObservations(): EffectiveIntegrationObservations {
  return new Map();
}

/** Record the definition ids resolved for an agent this tick. */
export function recordEffectiveIntegrationIds(
  observations: EffectiveIntegrationObservations,
  agentId: string,
  definitionIds: Iterable<string>,
): void {
  observations.set(agentId, new Set(definitionIds));
}

/** Forget an agent (paused, revoked, unassigned), so no stale belief survives it. */
export function forgetEffectiveIntegrations(
  observations: EffectiveIntegrationObservations,
  agentId: string,
): void {
  observations.delete(agentId);
}

